import React, { useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Star, Clock, Bike, MapPin, Plus, ChevronLeft, ShoppingBag, Info } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import CartDrawer from '../components/CartDrawer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { restaurants } from '../mock/mock';
import { useCart } from '../context/CartContext';
import { useToast } from '../hooks/use-toast';

const RestaurantDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem, count, setIsOpen } = useCart();
  const { toast } = useToast();
  const restaurant = restaurants.find((r) => r.id === id);
  const [activeSection, setActiveSection] = useState(null);

  const sections = useMemo(() => {
    if (!restaurant) return [];
    const map = {};
    restaurant.menu.forEach((dish) => {
      if (!map[dish.section]) map[dish.section] = [];
      map[dish.section].push(dish);
    });
    return Object.entries(map).map(([name, dishes]) => ({ name, dishes }));
  }, [restaurant]);

  if (!restaurant) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="mx-auto max-w-7xl px-4 py-24 text-center">
          <p className="text-muted-foreground">Ristorante non trovato.</p>
          <Link to="/" className="text-primary font-semibold mt-2 inline-block">Torna alla home</Link>
        </div>
      </div>
    );
  }

  const handleAdd = (dish) => {
    addItem(dish, restaurant);
    toast({ title: 'Aggiunto al carrello', description: `${dish.name} · ${dish.price.toFixed(2)} €` });
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="relative h-56 sm:h-72">
        <img src={restaurant.image} alt={restaurant.name} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/20" />
        <button onClick={() => navigate('/')} className="absolute top-4 left-4 flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-2 text-sm font-medium text-foreground shadow hover:bg-white transition-colors">
          <ChevronLeft className="h-4 w-4" /> Indietro
        </button>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="-mt-16 relative bg-white rounded-2xl border border-border shadow-sm p-6">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div>
              {restaurant.promo && <Badge className="bg-accent text-accent-foreground hover:bg-accent border-0 mb-2">{restaurant.promo}</Badge>}
              <h1 className="font-display text-3xl font-extrabold text-foreground">{restaurant.name}</h1>
              <p className="text-muted-foreground mt-1">{restaurant.cuisine}</p>
              <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
                <span className="flex items-center gap-1.5 font-semibold text-foreground"><Star className="h-4 w-4 fill-primary text-primary" /> {restaurant.rating} <span className="font-normal text-muted-foreground">({restaurant.reviews}+)</span></span>
                <span className="flex items-center gap-1.5 text-muted-foreground"><Clock className="h-4 w-4 text-primary" /> {restaurant.deliveryTime}</span>
                <span className="flex items-center gap-1.5 text-muted-foreground"><Bike className="h-4 w-4 text-primary" /> {restaurant.deliveryFee === 0 ? 'Consegna gratis' : `${restaurant.deliveryFee.toFixed(2)} €`}</span>
                <span className="flex items-center gap-1.5 text-muted-foreground"><MapPin className="h-4 w-4 text-primary" /> {restaurant.address}</span>
              </div>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2 rounded-xl bg-secondary/70 px-4 py-3 text-sm text-muted-foreground">
            <Info className="h-4 w-4 text-primary shrink-0" /> Ordine minimo {restaurant.minOrder.toFixed(2)} € · Pagamento alla consegna o online
          </div>
        </div>

        <div className="grid lg:grid-cols-[220px_1fr] gap-8 mt-8">
          <aside className="hidden lg:block">
            <div className="sticky top-20 space-y-1">
              <p className="font-display font-bold text-foreground mb-2">Menu</p>
              {sections.map((s) => (
                <button
                  key={s.name}
                  onClick={() => {
                    setActiveSection(s.name);
                    document.getElementById(`sec-${s.name}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className={`block w-full text-left rounded-lg px-3 py-2 text-sm transition-colors ${activeSection === s.name ? 'bg-accent text-primary font-semibold' : 'text-muted-foreground hover:bg-secondary'}`}
                >
                  {s.name}
                </button>
              ))}
            </div>
          </aside>

          <div className="space-y-10">
            {sections.map((s) => (
              <div key={s.name} id={`sec-${s.name}`} className="scroll-mt-20">
                <h2 className="font-display text-2xl font-bold text-foreground mb-4">{s.name}</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {s.dishes.map((dish) => (
                    <div key={dish.id} className="flex gap-4 rounded-2xl border border-border bg-white p-4 hover:border-primary/40 transition-colors">
                      <div className="flex-1">
                        <p className="font-semibold text-foreground">{dish.name}</p>
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{dish.description}</p>
                        <p className="mt-2 font-display font-bold text-foreground">{dish.price.toFixed(2)} €</p>
                      </div>
                      <div className="relative shrink-0">
                        {dish.image ? (
                          <img src={dish.image} alt={dish.name} className="h-24 w-24 rounded-xl object-cover" />
                        ) : (
                          <div className="h-24 w-24 rounded-xl bg-secondary" />
                        )}
                        <button
                          onClick={() => handleAdd(dish)}
                          className="absolute -bottom-2 -right-2 flex h-9 w-9 items-center justify-center rounded-full bg-primary text-white shadow-lg hover:bg-primary/90 transition-colors"
                          aria-label={`Aggiungi ${dish.name}`}
                        >
                          <Plus className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {count > 0 && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-5 left-1/2 -translate-x-1/2 z-30 flex items-center gap-3 rounded-full bg-primary px-6 py-3.5 text-white shadow-2xl hover:bg-primary/90 transition-colors lg:hidden"
        >
          <ShoppingBag className="h-5 w-5" /> Vedi carrello ({count})
        </button>
      )}

      <Footer />
      <CartDrawer />
    </div>
  );
};

export default RestaurantDetail;
